import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Zap, FileText, Clock, Shield } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';

/**
 * Upgrade Prompt Component
 * 
 * Modal prompting users to upgrade to Pro
 */
const UpgradePrompt = ({
  isOpen,
  onClose,
  reason = 'limit',
  className = '',
}) => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onClose();
    navigate('/pricing');
  };

  const reasons = {
    limit: {
      title: 'You\'ve hit your limit!',
      description: 'Upgrade to Pro for unlimited calculations and premium features.',
    },
    feature: {
      title: 'Pro Feature',
      description: 'This feature is only available on the Pro plan.',
    },
    export: {
      title: 'Export to PDF',
      description: 'Upgrade to Pro to export your calculations as PDF documents.',
    },
  };

  const content = reasons[reason] || reasons.limit;

  const features = [
    { icon: Zap, text: 'Unlimited calculations' },
    { icon: FileText, text: 'Export to PDF' },
    { icon: Clock, text: 'Priority support' },
    { icon: Shield, text: 'No ads' },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      className={className}
    >
      <div className="text-center">
        {/* Icon */}
        <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <Sparkles className="w-8 h-8 text-white" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {content.title}
        </h2>
        <p className="text-gray-600 mb-8">
          {content.description}
        </p>

        {/* Features */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          {features.map((feature, idx) => (
            <div 
              key={idx}
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
            >
              <feature.icon className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-700">{feature.text}</span>
            </div>
          ))}
        </div>

        {/* Price */}
        <div className="mb-6">
          <span className="text-3xl font-bold text-gray-900">$12</span>
          <span className="text-gray-500">/month</span>
          <p className="text-sm text-gray-500 mt-1">
            or $115/year (save $29)
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <Button
            variant="gradient"
            size="lg"
            fullWidth
            onClick={handleUpgrade}
            icon={Sparkles}
          >
            Upgrade to Pro
          </Button>
          <button
            onClick={onClose}
            className="w-full text-center text-gray-700 hover:text-gray-900 text-sm font-medium py-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400/30 rounded"
            aria-label="Close upgrade prompt"
          >
            Maybe later
          </button>
        </div>

        {/* Guarantee */}
        <p className="text-xs text-gray-400 mt-6">
          30-day money-back guarantee • Cancel anytime
        </p>
      </div>
    </Modal>
  );
};

export default UpgradePrompt;
